package com.example.mystybag.appCore.views.components

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Surface
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import com.example.mystybag.appCore.model.pages.ReviewCategoryConfigs
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.core.expected.format
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p4
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.ui.theme.LocalTheme.p12
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p20
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.views.AppTheme
import androidx.compose.ui.graphics.ColorFilter
import dev.icerock.moko.resources.compose.painterResource
import androidx.compose.foundation.Image

/** Shared dialog: "Общ рейтинг" with star, "X.X • N отзива", breakdown by category or "Няма налични оценки". */
@Composable
fun RatingDetailsDialog(
    averageRating: Double,
    reviewCount: Int,
    businessCategoryId: Long?,
    ratingCategory1: Double,
    ratingCategory2: Double,
    ratingCategory3: Double,
    ratingCategory4: Double,
    onDismiss: () -> Unit
) {
    Dialog(onDismissRequest = onDismiss) {
        Surface(
            shape = RoundedCornerShape(p16),
            color = AppTheme.current.colors.surface
        ) {
            Column(
                modifier = Modifier.padding(p24)
            ) {
                Texts.TitleLarge(
                    text = sharedString(SR.strings.overall_rating),
                    color = AppTheme.current.colors.onSurface
                )
                Spacer(modifier = Modifier.height(p8))
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(p4)
                ) {
                    Image(
                        painter = painterResource(SRC.images.home_top_pick_star),
                        contentDescription = null,
                        modifier = Modifier.size(20.dp),
                        colorFilter = ColorFilter.tint(AppTheme.current.colors.onSurface)
                    )
                    Texts.TitleSmall(
                        text = format("%.1f", averageRating),
                        color = AppTheme.current.colors.onSurface
                    )
                    Texts.BodyMedium(
                        text = " • ${sharedString(SR.strings.reviews_count, reviewCount)}",
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                }
                Spacer(modifier = Modifier.height(p16))
                if (reviewCount > 0) {
                    val reviewCategories = ReviewCategoryConfigs.getCategoriesForBusinessCategory(businessCategoryId)
                    val ratings = listOf(ratingCategory1, ratingCategory2, ratingCategory3, ratingCategory4)
                    reviewCategories.forEachIndexed { index, category ->
                        if (index > 0) Spacer(modifier = Modifier.height(p12))
                        RatingBreakdownItem(
                            label = getReviewCategoryLabel(category.labelKey),
                            rating = ratings.getOrNull(index) ?: 0.0
                        )
                    }
                } else {
                    Texts.BodyMedium(
                        text = sharedString(SR.strings.no_ratings_found),
                        color = AppTheme.current.colors.onSurfaceVariant
                    )
                }
                Spacer(modifier = Modifier.height(p20))
                TextButton(
                    onClick = onDismiss
                ) {
                    Texts.LabelLarge(
                        text = sharedString(SR.strings.close),
                        color = AppTheme.current.colors.primary
                    )
                }
            }
        }
    }
}

@Composable
fun getReviewCategoryLabel(labelKey: String): String {
    return try {
        when (labelKey) {
            "collection_experience" -> sharedString(SR.strings.collection_experience)
            "food_quality" -> sharedString(SR.strings.food_quality)
            "variety_of_contents" -> sharedString(SR.strings.variety_of_contents)
            "food_quantity" -> sharedString(SR.strings.food_quantity)
            "review_rating_freshness" -> sharedString(SR.strings.review_rating_freshness)
            "review_rating_quality" -> sharedString(SR.strings.review_rating_quality)
            "review_rating_variety" -> sharedString(SR.strings.review_rating_variety)
            "review_rating_value" -> sharedString(SR.strings.review_rating_value)
            "review_rating_taste" -> sharedString(SR.strings.review_rating_taste)
            "review_rating_presentation" -> sharedString(SR.strings.review_rating_presentation)
            "review_rating_portion_size" -> sharedString(SR.strings.review_rating_portion_size)
            "review_rating_coffee_quality" -> sharedString(SR.strings.review_rating_coffee_quality)
            "review_rating_service" -> sharedString(SR.strings.review_rating_service)
            "review_rating_atmosphere" -> sharedString(SR.strings.review_rating_atmosphere)
            "review_rating_service_speed" -> sharedString(SR.strings.review_rating_service_speed)
            "review_rating_product_quality" -> sharedString(SR.strings.review_rating_product_quality)
            "review_rating_packaging" -> sharedString(SR.strings.review_rating_packaging)
            "review_rating_service_quality" -> sharedString(SR.strings.review_rating_service_quality)
            "review_rating_professionalism" -> sharedString(SR.strings.review_rating_professionalism)
            "review_rating_hygiene" -> sharedString(SR.strings.review_rating_hygiene)
            "review_rating_price" -> sharedString(SR.strings.review_rating_price)
            "review_rating_accessibility" -> sharedString(SR.strings.review_rating_accessibility)
            "review_rating_effectiveness" -> sharedString(SR.strings.review_rating_effectiveness)
            "review_rating_service_consultation" -> sharedString(SR.strings.review_rating_service_consultation)
            "review_rating_packaging_transport" -> sharedString(SR.strings.review_rating_packaging_transport)
            else -> labelKey
        }
    } catch (e: Exception) {
        labelKey
    }
}

@Composable
fun RatingBreakdownItem(label: String, rating: Double) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Texts.LabelMedium(text = label, color = AppTheme.current.colors.onSurface)
        Row(
            horizontalArrangement = Arrangement.spacedBy(p8),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Box(
                modifier = Modifier
                    .width(120.dp)
                    .height(8.dp)
                    .clip(RoundedCornerShape(4.dp))
                    .background(AppTheme.current.colors.surfaceVariant)
            ) {
                Box(
                    modifier = Modifier
                        .fillMaxWidth((rating / 5.0).coerceIn(0.0, 1.0).toFloat())
                        .fillMaxSize()
                        .clip(RoundedCornerShape(4.dp))
                        .background(AppTheme.current.colors.primary)
                )
            }
            Texts.LabelMedium(
                text = format("%.1f", rating),
                color = AppTheme.current.colors.onSurface
            )
        }
    }
}
